import { Link } from 'react-router-dom'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { TOUR_DATES } from '../data'
import type { TourDate } from '../types'
import { useMediaQuery, breakpoints } from '../hooks/useMediaQuery'

function parseDateParts(iso: string): { day: string; month: string } {
  const d = new Date(iso + 'T00:00:00')
  return {
    day: d.toLocaleDateString('en-US', { day: '2-digit' }),
    month: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
  }
}

const TYPE_COLORS: Record<TourDate['type'], string> = {
  Festival: '#c8a96e',
  'Club Night': '#7a9ec8',
  Headline: '#c8a96e',
  'Residency Night': '#9ec87a',
  Private: '#c87a9e',
  'Special Event': '#c8a96e',
}

export default function TourSection({ limit = 5 }: { limit?: number }) {
  const isDesktop = useMediaQuery(breakpoints.md)
  const dates = TOUR_DATES.slice(0, limit)

  return (
    <section style={{ padding: '80px 20px', background: 'var(--ink)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '48px',
            paddingBottom: '20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div className="section-label">On The Road</div>
            <div className="section-title">Tour Dates</div>
          </div>
          <Link
            to="/tour"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '10px',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'var(--gold-dim)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderBottom: '1px solid rgba(122,101,64,0.4)',
              paddingBottom: '2px',
            }}
          >
            Full Schedule <ArrowRight size={12} />
          </Link>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--border)' }}>
          {dates.map((date) => {
            const { day, month } = parseDateParts(date.date)
            return (
              <div
                key={date.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? '90px 1fr auto auto' : '70px 1fr auto',
                  gap: isDesktop ? '24px' : '12px',
                  alignItems: 'center',
                  background: 'var(--ink)',
                  padding: isDesktop ? '18px 24px' : '14px 16px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'var(--ink-2)')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'var(--ink)')}
              >
                {/* Date */}
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: isDesktop ? '34px' : '26px', color: 'var(--gold)', lineHeight: 1 }}>
                    {day}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold-dim)', marginTop: '2px' }}>
                    {month}
                  </div>
                </div>

                {/* Venue */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: isDesktop ? '16px' : '13px', color: 'var(--cream)', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {date.venue}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '3px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    {date.city}, {date.state}
                  </div>
                </div>

                {/* Type — desktop only */}
                {isDesktop && (
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase', color: TYPE_COLORS[date.type] || 'var(--muted)', border: `1px solid ${TYPE_COLORS[date.type] || 'var(--muted)'}33`, padding: '5px 12px', whiteSpace: 'nowrap' }}>
                    {date.type}
                  </div>
                )}

                {/* Ticket */}
                {date.soldOut ? (
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--muted)', opacity: 0.5, width: isDesktop ? '100px' : 'auto', textAlign: 'center' }}>
                    Sold Out
                  </div>
                ) : (
                  <a
                    href={date.ticketUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontFamily: 'var(--font-display)', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold)', border: '1px solid rgba(200,169,110,0.35)', background: 'none', padding: '7px 0', width: isDesktop ? '100px' : '72px', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flexShrink: 0 }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(200,169,110,0.1)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'none')}
                  >
                    Tickets {isDesktop && <ExternalLink size={10} />}
                  </a>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
